
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useInvoiceData = () => {
  return useQuery({
    queryKey: ["invoices-with-schedules"],
    queryFn: async () => {
      const { data: invoices, error: invoicesError } = await supabase
        .from("invoices")
        .select("*")
        .order("created_at", { ascending: false });

      if (invoicesError) throw invoicesError;
      if (!invoices) return [];

      const { data: schedules, error: schedulesError } = await supabase
        .from("invoice_schedules")
        .select("invoice_id, recipients_emails, initial_template_id, first_reminder_template_id, second_reminder_template_id, final_reminder_template_id, sent_initial, sent_first_reminder, sent_second_reminder, sent_final_reminder");

      if (schedulesError) throw schedulesError;
      if (!schedules) return invoices.map(invoice => ({ ...invoice, isScheduled: false }));

      // Create a map of invoice_ids to their scheduled status, recipient emails, template info and email status
      const scheduledInvoiceMap = new Map();
      schedules.forEach(s => {
        scheduledInvoiceMap.set(s.invoice_id, {
          isScheduled: true,
          scheduledRecipients: s.recipients_emails || [],
          templateInfo: {
            initialTemplateId: s.initial_template_id,
            firstReminderTemplateId: s.first_reminder_template_id,
            secondReminderTemplateId: s.second_reminder_template_id,
            finalReminderTemplateId: s.final_reminder_template_id
          },
          emailStatus: {
            sentInitial: s.sent_initial || false,
            sentFirstReminder: s.sent_first_reminder || false,
            sentSecondReminder: s.sent_second_reminder || false, 
            sentFinalReminder: s.sent_final_reminder || false
          }
        });
      });

      // Get template names for easier reference
      const { data: templates, error: templatesError } = await supabase
        .from("email_templates")
        .select("id, name");
        
      if (templatesError) throw templatesError;
      
      // Create a map of template IDs to their names
      const templateMap = new Map();
      if (templates) {
        templates.forEach(t => {
          templateMap.set(t.id, t.name);
        });
      }

      return invoices.map(invoice => {
        const scheduleInfo = scheduledInvoiceMap.get(invoice.id) || { 
          isScheduled: false, 
          scheduledRecipients: [],
          templateInfo: {},
          emailStatus: {
            sentInitial: false,
            sentFirstReminder: false,
            sentSecondReminder: false,
            sentFinalReminder: false
          }
        };
        
        return {
          ...invoice,
          isScheduled: scheduleInfo.isScheduled,
          scheduledRecipients: scheduleInfo.scheduledRecipients,
          templateInfo: scheduleInfo.templateInfo,
          emailStatus: scheduleInfo.emailStatus,
          templateNames: scheduleInfo.templateInfo ? {
            initialTemplate: templateMap.get(scheduleInfo.templateInfo.initialTemplateId) || "Default",
            firstReminderTemplate: templateMap.get(scheduleInfo.templateInfo.firstReminderTemplateId) || "Default",
            secondReminderTemplate: templateMap.get(scheduleInfo.templateInfo.secondReminderTemplateId) || "Default",
            finalReminderTemplate: templateMap.get(scheduleInfo.templateInfo.finalReminderTemplateId) || "Default"
          } : null
        };
      });
    },
  });
};
